/* wb-product-reviews.js — les avis servis en HTML sur les fiches produit (30/09/2026).
   Copie de référence du dépôt parent : l'original déployé est
   `assets/wb-product-reviews.js` du thème live (en cas de doute le thème live fait
   foi). Chargé en `defer` par `sections/wb-product-reviews.liquid` elle-même, donc
   seulement sur une fiche notée qui a ses données de widget.

   La section rend les avis en HTML (`[data-wb-reviews]`, lus dans le métachamp
   `judgeme.review_widget_data`) et ne rend plus le widget Vue de Judge.me : le
   loader de l'app ne voit aucun widget d'avis, il ne charge ni Vue, ni ses CSS, ni
   la modale (audit PDP du 26/09, A2 : 100 requêtes et 505 Ko pour un bloc sous la
   ligne de flottaison). Rien n'est remplacé à l'écran :
   - « Voir plus » ajoute la page suivante à la liste, lue sur l'API publique que
     le widget interroge lui-même (`reviews/reviews_for_widget`) ;
   - le tri et le filtre par note (lignes de l'histogramme) relisent la page 1 sur
     la même API et remplacent la liste ;
   - « Plus pertinents », le tri d'ouverture (01/10), range les plus longs d'abord :
     tous les avis sont lus puis triés par `assets/wb-product-reviews-length.js`, et
     les cinq plus longs remplacent ceux de la section à son approche ;
   - les photos d'avis s'ouvrent dans une visionneuse (`<dialog>` de la section,
     `assets/wb-product-reviews-lightbox.js`) ;
   - « Écrire un avis » ouvre la modale héritée de Judge.me comme le fait le
     gestionnaire du widget (`handleWriteReview`), sans rien monter ;
   - un lien d'e-mail de demande d'avis ouvre le formulaire de lui-même, comme
     `shouldAutoOpenForm` du gestionnaire — c'est par là qu'arrivent les avis.
   ⚠️ Premier montage (30/09 matin) abandonné : il montait le widget Vue au clic
   et substituait le bloc — la page semblait se recharger (retour de Simon).
   Le badge étoiles du haut de fiche reste géré par `assets/app_judgeme.js`. */
(function () {
  'use strict';

  var BLOCK_SELECTOR = '[data-wb-reviews]';

  /* Même cible que le badge du haut de fiche (`assets/app_judgeme.js`) : la
     section, dont le `scroll-margin-top` réserve la hauteur du header collant. */
  function scrollToReviewsSection(el) {
    var section = el.closest('[data-wb-anchor-label]');
    if (!section || typeof section.scrollIntoView !== 'function') return false;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }

  /* ─────────────── Avis servis en HTML ─────────────── */
  var MORE_SELECTOR = '[data-wb-reviews-more]';
  var WRITE_SELECTOR = '[data-wb-reviews-write]';
  var LIST_SELECTOR = '[data-wb-reviews-list]';
  var ITEM_TEMPLATE_SELECTOR = 'template[data-wb-review-template]';
  var BUSY_ATTR = 'data-wb-reviews-busy';
  /* Repli si le loader n'a pas posé `jdgm.CDN_API_HOST` : l'hôte que le widget
     interroge (`api_client-*.js`), réponse cachée 20 min et ouverte en CORS. */
  var API_FALLBACK = 'https://cdn.judge.me/';
  /* Le fuseau du filtre `date` de la section, celui de la boutique : mesuré le
     30/09, un avis du 03/01/2023 à 23:00 UTC s'affiche le 04/01 (minuit à Paris).
     Les avis de la page 1 et ceux ajoutés portent ainsi la même date. */
  var DATE_TIME_ZONE = 'Europe/Paris';
  var FETCH_TIMEOUT_MS = 10000;
  var CSS_WAIT_MS = 3000;
  var CORE_TIMEOUT_MS = 10000;
  var MODAL_TIMEOUT_MS = 10000;
  var POLL_MS = 50;
  /* Les paramètres des liens des e-mails de demande d'avis : ceux de
     `shouldAutoOpenForm` du gestionnaire du widget, qui ouvre alors le formulaire. */
  var REVIEW_REQUEST_PARAMS = ['judgeme_token', 'judgeme_dynamic_form', 'judgeme_follow_up_token',
    'judgeme_upload_pictures', 'judgeme_pre_verified'];

  /* Le spinner du thème (`snippets/loading-spinner`, `.btn--loading`) ;
     `aria-disabled` plutôt que `disabled`, pour garder le focus clavier. */
  function setBusy(button, busy) {
    button.classList.toggle('btn--loading', busy);
    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute(BUSY_ATTR, '');
    } else {
      button.removeAttribute('aria-busy');
      button.removeAttribute('aria-disabled');
      button.removeAttribute(BUSY_ATTR);
    }
    var spinner = button.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !busy);
  }

  function setError(block, kind, visible) {
    var message = block.querySelector('[data-wb-reviews-error="' + kind + '"]');
    if (message) message.hidden = !visible;
  }

  /* Attend qu'une condition devienne vraie : Judge.me ne signale ni la fin du
     chargement de ses scripts, ni la définition de sa modale. */
  function waitFor(test, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var started = Date.now();
      (function poll() {
        var value;
        try { value = test(); } catch (e) { value = null; }
        if (value) { resolve(value); return; }
        if (Date.now() - started >= timeoutMs) { reject(new Error('délai dépassé')); return; }
        setTimeout(poll, POLL_MS);
      })();
    });
  }

  /* ── Liste : « Voir plus », tri, filtre par note ── */

  var MORE_WRAP_SELECTOR = '[data-wb-reviews-more-wrap]';
  var SORT_SELECTOR = '[data-wb-reviews-sort]';
  var RATING_SELECTOR = '[data-wb-reviews-rating]';
  var CLEAR_SELECTOR = '[data-wb-reviews-clear]';
  var FILTER_SELECTOR = '[data-wb-reviews-filter]';
  var EMPTY_SELECTOR = '[data-wb-reviews-empty]';
  var STATUS_SELECTOR = '[data-wb-reviews-status]';
  var MAX_PHOTOS = 6; // comme la boucle de la section (`limit: 6`)
  var FALLBACK_SORT = 'created_at';
  var LOCAL_PAGE_SIZE = 5; // comme la section (`per_page` du métachamp)
  var PRELOAD_MARGIN = '200% 0px';
  // Posé quand la liste suit l'ordre de TOUS les avis de la fiche, pas seulement des cinq de la section.
  var LONGEST_ATTR = 'data-wb-longest';
  /* Les tris du menu, traduits en paramètres d'API comme le fait le widget
     (`_prepareSortParams` de `review_api-*.js`, table `n` de `widget_common.js`).
     `local` : trié ici par `assets/wb-product-reviews-length.js` (« Plus pertinents »
     = les plus longs d'abord, 01/10) ; sans ce fichier, le tri de Judge.me (`by`). */
  var SORTS = {
    created_at: { by: 'created_at', dir: 'desc' },
    most_relevant: { by: 'most_relevant', local: true },
    highest_rating: { by: 'rating', dir: 'desc' },
    lowest_rating: { by: 'rating', dir: 'asc' },
    with_pictures: { by: 'with_pictures' },
  };

  /* Le tri des avis rendus par la section : celui du réglage Judge.me au moment où
     le métachamp a été écrit (`sort_key`), que la section pose en `data-default-sort`.
     `data-sort` ne porte que l'écart à ce tri. */
  function defaultSort(block) {
    var sort = block.getAttribute('data-default-sort');
    return SORTS[sort] ? sort : FALLBACK_SORT;
  }

  function currentQuery(block) {
    var sort = block.getAttribute('data-sort');
    return {
      sort: SORTS[sort] ? sort : defaultSort(block),
      rating: block.getAttribute('data-rating') || '',
    };
  }

  function lengthSort() {
    return window.WBReviewsLength || null;
  }

  function isLocal(sort) {
    return !!(SORTS[sort].local && lengthSort());
  }

  /* `all` : une page de 30 avis, plus récents d'abord et sans filtre, pour le tri
     fait ici ; sinon la page du tri et du filtre courants. */
  function reviewsUrl(block, page, all) {
    var jdgm = window.jdgm || {};
    var settings = window.jdgmSettings || {};
    var query = currentQuery(block);
    var sort = all ? SORTS.created_at : SORTS[query.sort];
    var values = {
      shop_domain: block.getAttribute('data-shop-domain'),
      platform: settings.platform || 'shopify',
      product_id: block.getAttribute('data-product-id'),
      page: String(page),
      per_page: all ? String(lengthSort().API_PAGE_SIZE) : '',
      sort_by: sort.by,
      sort_dir: sort.dir,
      translation_locale: block.getAttribute('data-locale'),
      // Même clé de cache que le widget (`ts: this.metafieldUpdatedAt`).
      ts: block.getAttribute('data-updated-at'),
    };
    // Comme le widget, une valeur vide n'est pas envoyée (autre clé de cache CDN).
    var params = new URLSearchParams();
    Object.keys(values).forEach(function (name) {
      if (values[name]) params.append(name, values[name]);
    });
    if (query.rating && !all) params.append('filter_rating[]', query.rating);
    return (jdgm.CDN_API_HOST || API_FALLBACK) + 'reviews/reviews_for_widget?' + params.toString();
  }

  /* Délai borné : une requête pendante laisserait le bouton en chargement pour
     toujours, sans message. */
  function fetchPage(block, page, all) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS) : null;
    var options = { headers: { Accept: 'application/json' } };
    if (controller) options.signal = controller.signal;
    return fetch(reviewsUrl(block, page, all), options)
      .then(function (response) {
        if (!response.ok) throw new Error('reviews_for_widget : HTTP ' + response.status);
        return response.json();
      })
      .then(function (data) {
        clearTimeout(timer);
        return data;
      }, function (error) {
        clearTimeout(timer);
        throw error;
      });
  }

  /* Chaque lecture porte un numéro : une réponse qui arrive après un choix plus
     récent du visiteur (autre tri, autre note) est ignorée. */
  function nextRequest(block) {
    var n = (parseInt(block.getAttribute('data-wb-request'), 10) || 0) + 1;
    block.setAttribute('data-wb-request', String(n));
    return n;
  }

  function isCurrent(block, n) {
    return block.getAttribute('data-wb-request') === String(n);
  }

  /* Le texte de `body_html`, sans jamais injecter son HTML : un `<template>` est
     inerte (ni script, ni chargement d'image), on n'en lit que le texte. */
  function htmlToText(html) {
    var inert = document.createElement('template');
    // L'API écrit « …\n<br />… » : le saut qui précède le `<br />` ne compte pas double.
    inert.innerHTML = String(html || '').replace(/\s*<br\s*\/?>\s*/gi, '\n');
    var paragraphs = inert.content.querySelectorAll('p');
    if (!paragraphs.length) return inert.content.textContent.trim();
    return Array.prototype.map.call(paragraphs, function (p) { return p.textContent.trim(); })
      .filter(Boolean)
      .join('\n');
  }

  /* Mêmes trois essais de clé que la section (`pt-PT`, `pt-pt`, `pt`). */
  function translationFor(translations, uuid, locale) {
    var byLocale = (translations || {})[uuid] || {};
    var lower = locale.toLowerCase();
    return byLocale[locale] || byLocale[lower] || byLocale[lower.split('-')[0]] || null;
  }

  function dateParts(iso) {
    var parts = {};
    try {
      new Intl.DateTimeFormat('en-GB', {
        timeZone: DATE_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
      }).formatToParts(new Date(iso)).forEach(function (part) { parts[part.type] = part.value; });
    } catch (e) {
      // Date illisible : on la lit telle quelle plutôt que d'afficher « NaN ».
      var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
      parts = m ? { year: m[1], month: m[2], day: m[3] } : { year: '', month: '', day: '' };
    }
    return parts;
  }

  function setLines(el, text) {
    text.split('\n').forEach(function (line, i) {
      if (i > 0) el.appendChild(document.createElement('br'));
      el.appendChild(document.createTextNode(line));
    });
  }

  function removeEl(el) {
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  /* Seules des adresses https entrent dans la page : `src` d'une vignette et
     grande image de la visionneuse viennent de l'API. */
  function isSafePhoto(photo) {
    var big = photo && (photo.huge || photo.original);
    return !!(photo && /^https:\/\//.test(photo.small || '') && /^https:\/\//.test(big || ''));
  }

  /* La vignette que la section rend pour une photo d'avis : 160 px affichés en
     104 (assez pour un écran 1,5x), 1024 px dans la visionneuse. */
  function photoItem(photo, label) {
    var item = document.createElement('li');
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'wb-review__photo';
    button.setAttribute('data-wb-photo', photo.huge || photo.original);
    button.setAttribute('aria-label', label);
    var img = document.createElement('img');
    img.src = photo.small;
    img.alt = '';
    img.loading = 'lazy';
    img.width = 104;
    img.height = 104;
    button.appendChild(img);
    item.appendChild(button);
    return item;
  }

  /* Un avis de l'API, dans le gabarit que la section rend à côté de sa boucle :
     même structure qu'un avis servi, libellés déjà traduits par le thème. */
  function renderReview(template, review, context) {
    var li = document.importNode(template.content, true).querySelector('.wb-review');
    var original = htmlToText(review.body_html);
    var body = original;
    var title = review.title || '';
    var translated = false;
    var pageLang = context.locale.toLowerCase().split('-')[0];
    var reviewLang = String(review.language || '').toLowerCase().split('-')[0];
    if (reviewLang !== pageLang) {
      var t = translationFor(context.translations, review.uuid, context.locale);
      // Identique à l'original : langue mal détectée, pas une traduction.
      if (t && t.body && t.body !== original) {
        body = t.body;
        title = t.title || title;
        translated = true;
      }
    }

    var name = review.reviewer_name || '';
    li.setAttribute('data-wb-review', review.uuid);
    // Lue par `assets/wb-product-reviews-translate.js`, comme celle des avis de la section.
    li.setAttribute('data-wb-review-lang', review.language || '');
    li.querySelector('.wb-review__avatar').textContent = review.reviewer_initial || '';
    li.querySelector('.wb-review__name').textContent = name;
    if (!review.verified_buyer) removeEl(li.querySelector('.wb-review__verified'));

    var d = dateParts(review.created_at);
    var time = li.querySelector('.wb-review__date');
    time.setAttribute('datetime', d.year + '-' + d.month + '-' + d.day);
    time.textContent = context.dateFormat
      .replace('%d', d.day).replace('%m', d.month).replace('%Y', d.year);

    var rating = Number(review.rating) || 0;
    var stars = li.querySelector('.wb-reviews__stars');
    stars.setAttribute('aria-label', (stars.getAttribute('data-wb-label') || '').replace('%RATING%', String(rating)));
    stars.removeAttribute('data-wb-label');
    stars.style.setProperty('--wb-rating', String(rating));

    if (title) li.querySelector('.wb-review__title').textContent = title;
    else removeEl(li.querySelector('.wb-review__title'));
    setLines(li.querySelector('.wb-review__body'), body);
    if (!translated) removeEl(li.querySelector('.wb-review__translated'));

    var media = li.querySelector('.wb-review__media');
    var photos = (review.pictures_urls || []).filter(isSafePhoto).slice(0, MAX_PHOTOS);
    if (media && photos.length) {
      // Fonction de remplacement : un « $& » dans un nom resterait sinon un motif.
      var label = context.photoLabel.replace('%NAME%', function () { return name; });
      photos.forEach(function (photo) { media.appendChild(photoItem(photo, label)); });
    } else {
      removeEl(media);
    }
    return li;
  }

  /* Les `<li>` d'une réponse, sans rien insérer : si le rendu échoue, la liste
     affichée n'a pas encore été touchée. `shown` : uuids à ne pas répéter. */
  function buildItems(block, data, shown) {
    var template = block.querySelector(ITEM_TEMPLATE_SELECTOR);
    if (!template) throw new Error('gabarit d\'avis absent');
    var context = {
      locale: block.getAttribute('data-locale') || '',
      dateFormat: block.getAttribute('data-date-format') || '%d/%m/%Y',
      photoLabel: block.getAttribute('data-wb-photo-label') || '%NAME%',
      translations: data.review_translations,
    };
    var items = [];
    (data.reviews || []).forEach(function (review) {
      if (!review || !review.uuid || shown[review.uuid]) return;
      shown[review.uuid] = true;
      items.push(renderReview(template, review, context));
    });
    return items;
  }

  /* Les avis affichés, dans l'ordre. Un ensemble plutôt qu'un sélecteur : l'uuid
     n'a pas à être échappé. */
  function shownIds(block) {
    var shown = {};
    block.querySelectorAll(LIST_SELECTOR + ' > [data-wb-review]').forEach(function (li) {
      shown[li.getAttribute('data-wb-review')] = true;
    });
    return shown;
  }

  function appendPage(block, data) {
    var list = block.querySelector(LIST_SELECTOR);
    // Un avis publié entre deux clics décale les pages : pas de doublon.
    var added = buildItems(block, data, shownIds(block));
    added.forEach(function (li) { list.appendChild(li); });
    return added;
  }

  function hasMorePages(data, page) {
    var total = parseInt((data.pagination || {}).total_pages, 10) || 0;
    return page < total && (data.reviews || []).length > 0;
  }

  /* Tous les avis de la fiche, triés par longueur : lus une fois par page vue. */
  var loadAllReviews = null;
  function allReviews(block) {
    if (!loadAllReviews) {
      loadAllReviews = lengthSort().createLoader(function (page) { return fetchPage(block, page, true); }, htmlToText);
    }
    return loadAllReviews();
  }

  /* Une page du tri courant, au format de l'API, avec `more` (y a-t-il une suite)
     et `page` (le numéro lu). Trié ici : la suite des avis pas encore affichés
     (`shown`, ou la liste telle qu'elle est à l'arrivée des avis). Si la liste est
     encore celle de la section, qui n'a trié que ses cinq avis, `replace` : elle est
     refaite dans l'ordre de toute la fiche, avec la suite — sinon les plus longs
     viendraient après des avis courts (constaté par Simon sur la recette, 01/10). */
  function readPage(block, page, shown) {
    var query = currentQuery(block);
    if (!isLocal(query.sort)) {
      return fetchPage(block, page).then(function (data) {
        return { data: data, more: hasMorePages(data, page), page: (data.pagination || {}).current_page || page };
      });
    }
    return allReviews(block).then(function (all) {
      var current = shown || shownIds(block);
      if (page > 1 && !block.hasAttribute(LONGEST_ATTR)) {
        var count = Object.keys(current).length + LOCAL_PAGE_SIZE;
        var whole = lengthSort().select(all, { rating: query.rating, count: count });
        return { data: whole, more: whole.more, page: page, replace: true };
      }
      var picked = lengthSort().select(all, { rating: query.rating, shown: current, count: LOCAL_PAGE_SIZE });
      return { data: picked, more: picked.more, page: page };
    });
  }

  function replaceList(block, items) {
    var list = block.querySelector(LIST_SELECTOR);
    while (list.firstChild) list.removeChild(list.firstChild);
    items.forEach(function (li) { list.appendChild(li); });
    return items;
  }

  /* Un conteneur, pas le bouton : `.btn` du thème pose un `display` qui battrait
     l'attribut `hidden` sur le bouton lui-même. */
  function setMoreVisible(block, visible) {
    var wrap = block.querySelector(MORE_WRAP_SELECTOR);
    if (wrap) wrap.hidden = !visible;
  }

  function loadMore(block, button) {
    var list = block.querySelector(LIST_SELECTOR);
    if (button.hasAttribute(BUSY_ATTR) || list.hasAttribute('aria-busy')) return;
    var next = (parseInt(block.getAttribute('data-page'), 10) || 1) + 1;
    var request = nextRequest(block);
    setBusy(button, true);
    readPage(block, next, null)
      .then(function (result) {
        setBusy(button, false);
        if (!isCurrent(block, request)) return;
        var before = list.children.length;
        var added = result.replace
          ? replaceList(block, buildItems(block, result.data, {})).slice(before)
          : appendPage(block, result.data);
        if (result.replace) block.setAttribute(LONGEST_ATTR, '');
        block.setAttribute('data-page', String(result.page));
        setError(block, 'list', false);
        if (result.more) return;
        /* Plus rien à lire : le bouton s'en va, et le focus clavier passe au
           premier avis ajouté plutôt que de retomber en haut de page. */
        setMoreVisible(block, false);
        var target = added[0] || block.querySelector(LIST_SELECTOR + ' > li:last-child');
        if (target) {
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
      })
      .catch(function () {
        setBusy(button, false);
        if (isCurrent(block, request)) setError(block, 'list', true);
      });
  }

  /* Reporte l'état du tri et du filtre sur les commandes : menu, lignes de
     l'histogramme enfoncées, « Effacer le filtre ». */
  function syncControls(block) {
    var query = currentQuery(block);
    var select = block.querySelector(SORT_SELECTOR);
    if (select) select.value = query.sort;
    block.querySelectorAll(RATING_SELECTOR).forEach(function (button) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-wb-reviews-rating') === query.rating));
    });
    var filter = block.querySelector(FILTER_SELECTOR);
    if (filter) filter.hidden = !query.rating;
  }

  function setQueryAttr(block, name, value) {
    if (value) block.setAttribute(name, value);
    else block.removeAttribute(name);
  }

  /* Ce qu'annonce la région `role="status"` une fois la liste remplacée : la
     ligne de la note filtrée (« 1 étoile, 4 avis »), sinon « Basé sur N avis ». */
  function resultLabel(block, rating) {
    var row = rating && block.querySelector('[data-wb-reviews-rating="' + rating + '"]');
    var source = row ? (row.querySelector('.visually-hidden') || row) : block.querySelector('.wb-reviews__based-on');
    return source ? source.textContent.trim() : '';
  }

  /* Tri ou filtre changé : la page 1 de ce choix remplace la liste. En cas
     d'échec, la liste reste, et les commandes reviennent à l'état de la liste
     RÉELLEMENT affichée (`data-shown-*`), pas à celui d'avant le dernier clic :
     deux choix rapprochés, le second en échec, laisseraient sinon le menu sur un
     tri que la liste ne montre pas. */
  function reload(block, change) {
    var list = block.querySelector(LIST_SELECTOR);
    Object.keys(change).forEach(function (name) { setQueryAttr(block, name, change[name]); });
    syncControls(block);
    var query = currentQuery(block);
    var request = nextRequest(block);
    list.setAttribute('aria-busy', 'true');
    setError(block, 'list', false);
    readPage(block, 1, {})
      .then(function (result) {
        if (!isCurrent(block, request)) return;
        var items = buildItems(block, result.data, {});
        list.removeAttribute('aria-busy');
        replaceList(block, items);
        if (isLocal(query.sort)) block.setAttribute(LONGEST_ATTR, '');
        else block.removeAttribute(LONGEST_ATTR);
        block.setAttribute('data-page', '1');
        setQueryAttr(block, 'data-shown-sort', query.sort === defaultSort(block) ? '' : query.sort);
        setQueryAttr(block, 'data-shown-rating', query.rating);
        var empty = block.querySelector(EMPTY_SELECTOR);
        if (empty) empty.hidden = items.length > 0;
        var status = block.querySelector(STATUS_SELECTOR);
        if (status) status.textContent = items.length ? resultLabel(block, query.rating) : (empty ? empty.textContent.trim() : '');
        setMoreVisible(block, result.more);
      })
      .catch(function () {
        if (!isCurrent(block, request)) return;
        list.removeAttribute('aria-busy');
        setQueryAttr(block, 'data-sort', block.getAttribute('data-shown-sort'));
        setQueryAttr(block, 'data-rating', block.getAttribute('data-shown-rating'));
        syncControls(block);
        setError(block, 'list', true);
      });
  }

  /* « Plus pertinents » à l'ouverture : la section n'a pu trier que ses cinq avis. Les
     plus longs de toute la fiche les remplacent, lus à l'approche de la section pour
     que l'échange se fasse hors de l'écran, sans état de chargement. */
  function whenNear(el, run) {
    if (typeof IntersectionObserver !== 'function') {
      run();
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      if (!entries.some(function (entry) { return entry.isIntersecting; })) return;
      observer.disconnect();
      run();
    }, { rootMargin: PRELOAD_MARGIN });
    observer.observe(el);
  }

  function showLongest(block, all) {
    var query = currentQuery(block);
    /* Un autre tri ou un filtre choisi : la liste est celle du visiteur. Liste déjà
       refaite (« Voir plus » arrivé avant) : rien à faire. « Voir plus » en cours,
       lui, enchaîne sur cette liste-ci. */
    if (block.hasAttribute(LONGEST_ATTR) || !isLocal(query.sort) || query.rating) return;
    var picked = lengthSort().select(all, { count: LOCAL_PAGE_SIZE });
    var wanted = picked.reviews.map(function (review) { return review.uuid; });
    if (wanted.join(',') !== Object.keys(shownIds(block)).join(',')) {
      replaceList(block, buildItems(block, picked, {}));
      setMoreVisible(block, picked.more);
    }
    block.setAttribute(LONGEST_ATTR, '');
  }

  function preloadLongest(block) {
    if (!isLocal(currentQuery(block).sort)) return;
    if (!((parseInt(block.getAttribute('data-total-pages'), 10) || 1) > 1)) return;
    whenNear(block, function () {
      allReviews(block)
        .then(function (all) { showLongest(block, all); })
        .catch(function () {
          /* Voulu sans message : la liste de la section reste affichée, juste, et
             « Voir plus » ou un tri relira les avis, avec message s'il échoue. */
        });
    });
  }

  /* « Effacer le filtre » disparaît avec le filtre : s'il avait le focus, il
     passe au menu de tri (ou à la 1re ligne de l'histogramme), pas au `body`. */
  function clearFilter(block, button) {
    var hadFocus = document.activeElement === button;
    reload(block, { 'data-rating': '' });
    if (!hadFocus) return;
    var next = block.querySelector(SORT_SELECTOR) || block.querySelector(RATING_SELECTOR);
    if (next) next.focus();
  }

  function toggleRating(block, button) {
    var rating = button.getAttribute('data-wb-reviews-rating');
    reload(block, { 'data-rating': currentQuery(block).rating === rating ? '' : rating });
  }

  /* ── Photos d'avis : la visionneuse est dans `assets/wb-product-reviews-lightbox.js` ── */

  var PHOTO_SELECTOR = '.wb-review__photo';
  var LIGHTBOX_SELECTOR = '[data-wb-lightbox]';

  function lightbox() {
    return window.WBReviewsLightbox || null;
  }

  /* Sans la visionneuse (fichier absent) : la photo dans un onglet. */
  function openPhoto(block, button) {
    if (lightbox()) {
      lightbox().open(block, button);
      return;
    }
    var url = button.getAttribute('data-wb-photo');
    if (url) window.open(url, '_blank', 'noopener');
  }

  /* ── « Écrire un avis » ── */

  /* La modale lit l'en-tête produit dans le DOM (`_getProductDataFromDOM` de
     `write_review_modal.js`) : titre sur `.jdgm-review-widget[data-product-title]`,
     image sur `.jm-review-widget[data-image-url]` — des éléments du widget, qui
     n'est plus là. On les pose au clic seulement, masqués : présents au
     chargement, ils feraient charger au loader la modale (142 Ko) et `main.css`. */
  function ensureProductReference(block) {
    if (document.querySelector('.jdgm-review-widget')) return;
    var reference = document.createElement('div');
    reference.className = 'jdgm-review-widget';
    reference.hidden = true;
    reference.setAttribute('aria-hidden', 'true');
    reference.setAttribute('data-product-id', block.getAttribute('data-product-id') || '');
    reference.setAttribute('data-product-title', block.getAttribute('data-product-title') || '');
    var image = document.createElement('div');
    image.className = 'jm-review-widget';
    image.setAttribute('data-image-url', block.getAttribute('data-product-image') || '');
    reference.appendChild(image);
    block.appendChild(reference);
  }

  function withinDelay(promise, ms) {
    return new Promise(function (resolve) {
      var timer = setTimeout(resolve, ms);
      var done = function () { clearTimeout(timer); resolve(); };
      promise.then(done, done);
    });
  }

  /* Les feuilles de la modale, demandées une fois par page. `jdgm.loadCSS`
     rappelle au chargement, mais jamais pour une feuille déjà demandée (le
     loader demande `form.css` lui-même quand on arrive d'un e-mail) : on ne
     l'attend alors pas, et l'attente reste plafonnée. La feuille `main.css`
     s'ajoute à celle du gestionnaire du widget : le loader ne la charge que
     s'il voit un widget d'avis, et il n'y en a plus. */
  var modalStyles = null;
  function loadModalStyles(jdgm) {
    if (!modalStyles) {
      var requested = (jdgm.loadCSS && jdgm.loadCSS.requestedUrls) || [];
      modalStyles = Promise.all(['form.css', 'main.css'].map(function (file) {
        var url = jdgm.widgetPath(file);
        if (requested.indexOf(url) >= 0) return Promise.resolve();
        return new Promise(function (resolve) { jdgm.loadCSS(url, resolve); });
      }));
    }
    return withinDelay(modalStyles, CSS_WAIT_MS);
  }

  /* Ce que fait `handleWriteReview` du gestionnaire du widget, pour nos
     réglages (`review_form_location` = in_store_popup, Sign in with Shop
     désactivé) : charger la modale héritée et ses feuilles, attendre le cœur
     (`jdgm.$`, que le loader charge pour le badge du haut de fiche), puis
     `new jdgm._WriteReviewModal(jdgm.$).setup(…)` et `showModalPage()` — ici
     une fois les feuilles chargées, pour ne pas montrer un formulaire brut.
     Données client : aucune, comme avec le widget du live, dont le conteneur
     ne portait pas de `data-customer-*` (vérifié le 30/09). */
  function openWriteForm(block, button) {
    var jdgm = window.jdgm;
    if (button && button.hasAttribute(BUSY_ATTR)) return;
    if (!jdgm || typeof jdgm.loadScript !== 'function' || typeof jdgm.widgetPath !== 'function'
      || typeof jdgm.loadCSS !== 'function' || !jdgm.CDN_HOST) {
      setError(block, 'write', true);
      return;
    }
    if (button) setBusy(button, true);
    var styles = null;

    waitFor(function () { return typeof jdgm.$ === 'function'; }, CORE_TIMEOUT_MS)
      .then(function () {
        jdgm.loadScript(jdgm.CDN_HOST + 'widget/write_review_modal.js');
        jdgm.loadScript(jdgm.CDN_HOST + 'widget/form.js');
        styles = loadModalStyles(jdgm);
        return waitFor(function () { return typeof jdgm._WriteReviewModal === 'function'; }, MODAL_TIMEOUT_MS);
      })
      .then(function () {
        document.querySelectorAll('.jdgm-review-widget-modal.jdgm-write-review-modal').forEach(removeEl);
        ensureProductReference(block);
        jdgm.customerInfo = jdgm.customerInfo || null;
        jdgm.shopConsentToken = null;
        var modal = new jdgm._WriteReviewModal(jdgm.$);
        return Promise.resolve(modal.setup('jdgm-review-widget-modal', block.getAttribute('data-product-id'),
          { siwsSignedIn: false }))
          .then(function (ready) {
            if (!ready) throw new Error('modale Judge.me non initialisée');
            return styles;
          })
          .then(function () { modal.showModalPage(); });
      })
      .then(function () {
        if (button) setBusy(button, false);
        setError(block, 'write', false);
      })
      .catch(function () {
        if (button) setBusy(button, false);
        setError(block, 'write', true);
      });
  }

  function arrivesFromReviewRequest() {
    if (window.location.hash === '#judgeme') return true;
    var params = new URLSearchParams(window.location.search);
    return REVIEW_REQUEST_PARAMS.some(function (name) { return params.has(name); });
  }

  function onBlockClick(event) {
    var block = event.currentTarget;
    var target = event.target;
    // Les clics dans la visionneuse sont les siens (`assets/wb-product-reviews-lightbox.js`).
    if (target.closest(LIGHTBOX_SELECTOR)) return;
    var more = target.closest(MORE_SELECTOR);
    var write = target.closest(WRITE_SELECTOR);
    var rating = target.closest(RATING_SELECTOR);
    var clear = target.closest(CLEAR_SELECTOR);
    var photo = target.closest(PHOTO_SELECTOR);
    if (!more && !write && !rating && !clear && !photo) return;
    event.preventDefault();
    if (more) loadMore(block, more);
    else if (write) openWriteForm(block, write);
    else if (rating) toggleRating(block, rating);
    else if (clear) clearFilter(block, clear);
    else openPhoto(block, photo);
  }

  function onBlockChange(event) {
    var select = event.target.closest(SORT_SELECTOR);
    if (!select) return;
    var block = event.currentTarget;
    reload(block, { 'data-sort': select.value === defaultSort(block) ? '' : select.value });
  }

  /* ─────────────── Amorçage ─────────────── */

  function init() {
    var block = document.querySelector(BLOCK_SELECTOR);
    if (!block) return;
    block.addEventListener('click', onBlockClick);
    block.addEventListener('change', onBlockChange);
    if (lightbox()) lightbox().bind(block, { toText: htmlToText, listSelector: LIST_SELECTOR });
    // Le navigateur peut restaurer un `<select>` au retour arrière : on le ramène
    // au tri réellement affiché.
    syncControls(block);
    preloadLongest(block);
    // Lien d'un e-mail de demande d'avis : le formulaire s'ouvre de lui-même.
    if (arrivesFromReviewRequest()) openWriteForm(block, block.querySelector(WRITE_SELECTOR));
    // Anciens liens vers l'ancre du widget, qui n'a plus de cible : la section.
    else if (window.location.hash === '#judgeme_product_reviews') scrollToReviewsSection(block);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
